import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography } from '@mui/material';
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import refresh from 'packages/novous_web/src/assets/icons/Refresh.svg';
import arrowUp from 'packages/novous_web/src/assets/icons/ArrowBlack.svg';
import arrowDown from 'packages/novous_web/src/assets/icons/ArrowDownBlack.svg';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';

interface FilterDataModelProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subTitle?: string;
  handleFilterSave: (data: any) => void;
  description?: string;
  trgtcData?: boolean;
}

interface GroupOption {
  code: string;
  label: string;
}

const FilterDeviceModal: React.FC<FilterDataModelProps> = ({
  open,
  onClose,
  title = 'Customise Table',
  subTitle = '',
  handleFilterSave,
  description = 'Choose which sections to display and arrange them the way you like.',
  trgtcData = false,
}) => {
  const { user } = useAuth();

  const {
    data: deviceData,
    error: deviceError,
    loading: deviceLoading,
    fetchData: fetchDeviceData,
  } = useAxios<any>(`${API_ENDPOINTS.dashboard.VEHICLE}`, 'POST');
  const {
    data: groupsData,
    error: groupsError,
    loading: groupsLoading,
    fetchData: fetchGroupsData,
  } = useAxios<{ data: any }>(`${API_ENDPOINTS.dashboard.GROUP}${user?.customer_id}`, 'GET');

  let deviceOptions: GroupOption[] = [];

  if (deviceData) {
    deviceOptions = deviceData.map((item: any) => {
      return {
        code: item.deviceRegistrationNo,
        deviceId: item.deviceId,
        deviceName: item.deviceRegistrationNo,
        label: item.deviceRegistrationNo,
      };
    });
  }

  useEffect(() => {
    if (user?.customer_id) {
      fetchDeviceData({ fleetId: user?.customer_id });
      fetchGroupsData();
    }
  }, [user?.customer_id]);

  let groupOptions: GroupOption[] = [];

  if (groupsData && groupsData?.data) {
    groupOptions = groupsData?.data.map((item: string) => {
      return { code: item, label: item };
    });
  }

  // Get filterData from location if available
  let filterDataFromLocation: any = { data: {}, isfiltered: false };
  if (window && window.history && window.history.state && window.history.state.usr && window.history.state.usr.filterData) {
    filterDataFromLocation = window.history.state.usr.filterData;
  }

  // Selections
  const [selectedGroup, setSelectedGroup] = useState<string | null>(
    filterDataFromLocation.data?.vehicleGroup || null
  );
  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);

  // Expanded/collapsed states
  const [dataTableExpanded, setDataTableExpanded] = useState<boolean>(false);
  const [deviceExpanded, setDeviceExpanded] = useState<boolean>(false);

  // Handlers
  const handleGroupChange = (newValue: string) => {
    setSelectedGroup(newValue);
  };

  const handleDeviceChange = (newValue: string) => {
    setSelectedDevice(newValue);
  };

  const toggleDataTableExpanded = () => {
    setDataTableExpanded((prev) => !prev);
  };

  const toggleDeviceExpanded = () => {
    setDeviceExpanded((prev) => !prev);
  };

  const handleReset = () => {
    setSelectedDevice(null);
    setSelectedGroup(null);
    handleFilterSave({
      group: null,
      device: null,
    });
  };

  const handleSave = () => {
    // Perform save logic (send selected filters to parent or API)
    console.log('Filters Applied:', {
      group: selectedGroup,
      device: selectedDevice,
    });
    handleFilterSave({
      group: selectedGroup,
      device: selectedDevice,
    });
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="customise-table-title"
      aria-describedby="customise-table-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: 'auto',
          height: '80vh',
        }}
      >
        <div className="flex justify-between  h-full flex-col">
          <div className="flex gap-8 flex-col">
            {/* Header */}
            <div className="flex flex-col gap-1">
              <div className="flex justify-between items-center">
                <Typography variant="h6">
                  {title} {subTitle}
                </Typography>
                <CustomIconButton
                  text="Clear All"
                  icon={<img src={refresh} alt="reset" />}
                  background="transparent"
                  variant="outlined"
                  onClick={handleReset}
                />
              </div>

              {description && (
                <Typography variant="button" fontWeight={400} color="secondary">
                  {description}
                </Typography>
              )}
            </div>

            {/* Groups section */}
            <div className="flex flex-col gap-2">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={toggleDataTableExpanded}
              >
                <Typography variant="body1">{trgtcData ? 'Depot' : 'Group'}</Typography>

                <img
                  src={dataTableExpanded ? arrowUp : arrowDown}
                  width={24}
                  height={24}
                  alt={dataTableExpanded ? 'collapse' : 'expand'}
                />
              </div>

              {dataTableExpanded && (
                <div className="max-w-[50%]">
                  <CustomDropDownInput
                    options={groupOptions.map(({ code, label }) => ({
                      value: code,
                      label,
                    }))}
                    value={selectedGroup || ''}
                    onChange={handleGroupChange}
                    placeholder={trgtcData ? 'Select Depot' : 'Select Group'}
                  />
                </div>
              )}
            </div>

            {/* Device Type section */}
            <div className="flex flex-col gap-2">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={toggleDeviceExpanded}
              >
                <Typography variant="body1">Device Type</Typography>

                <img
                  src={deviceExpanded ? arrowUp : arrowDown}
                  width={24}
                  height={24}
                  alt={deviceExpanded ? 'collapse' : 'expand'}
                />
              </div>

              {deviceExpanded && (
                <div className="max-w-[50%]">
                  <CustomDropDownInput
                    options={[
                      { value: 'fcw_dsm', label: 'FCW-DMS' },
                      { value: 'fcw', label: 'FCW' },
                      { value: 'dsm', label: 'DMS' },
                      { value: 'vts', label: 'VTS' },
                    ]}
                    value={selectedDevice || ''}
                    onChange={handleDeviceChange}
                    placeholder="Select Device"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4 items-center  justify-between">
            <CustomIconButton
              text="Cancel"
              background="transparent"
              outline
              padding="0px"
              variant="outlined"
              onClick={handleCancel}
            />
            <CustomIconButton text="Apply" outline padding="0px" variant="contained" onClick={handleSave} />
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default FilterDeviceModal;
